import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { rulesRecapSentence, stepLookupOf, stepTitle } from '@/lib/protocol-labels';
import {
  otherSteps,
  setAttempts,
  setEmptyAnswers,
  setFallback,
  setFallbackTarget,
  setFallbackText,
  setMaxTurns,
} from '@/lib/protocol-step-draft';
import type { ProtocolStepDraft, StepRules } from '@matter_extractor/shared';
import { PROTOCOL_CLOSE_TEXT_MAX, PROTOCOL_DEFAULT_CLOSE_TEXT } from '@matter_extractor/shared';
import { Icon, Select, TextField } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

/**
 * The « règles générales » of a step: the numbers the engine applies without a
 * condition being written (refonte modulaire §7.2, §8.3; maquette `rulesHTML`).
 *
 * ## The cap is required, and the panel says so before the box does
 *
 * A step without a turn cap is a refusal of « Enregistrer une nouvelle version »
 * (§7.1, H8), and the draft holds `null` because a step being typed has no cap
 * yet. So the field is yellow and carries the sentence: the refusal is met here,
 * where it can be fixed in one keystroke, rather than in a dialog three clicks
 * later that can only name it.
 *
 * ## The recap is generated and never typed
 *
 * `rulesRecapSentence` composes it from the four values, and it returns null
 * while the cap is missing - the panel then says the sentence will write itself,
 * rather than showing a sentence with a hole where a number belongs. It is the
 * one place the three numbers, the essais and the repli are read as ONE
 * behaviour, which is how §8.3 describes them.
 *
 * ## The budget is read-only here, on purpose
 *
 * §11.2 makes it ONE global value in euros applied to each step (H3). A number
 * shown beside the three others would read as a fourth one to set, so the line
 * names where it is set instead - and it names no amount, because this lot has
 * no Réglages page to read one from and an invented figure would be worse than
 * none.
 */

export interface StepRulesPanelProps {
  step: ProtocolStepDraft;
  /** Every step: « aller à une étape » chooses among the others. */
  steps: readonly ProtocolStepDraft[];
  readOnly: boolean;
  onChangeRules: (next: StepRules) => void;
}

/** The width of a number field of the panel: the package field fills its envelope. */
const NUMBER_BOX = 'w-20 shrink-0';

export function StepRulesPanel({ step, steps, readOnly, onChangeRules }: StepRulesPanelProps) {
  const rules = step.rules;
  const others = otherSteps(steps, step.id);
  const alone = others.length === 0;
  const recap = rulesRecapSentence(rules, stepLookupOf(steps));

  return (
    <div>
      <KeyValue
        label="plafond de tours"
        sub="requis · pas de minimum"
        control={
          // No warning state in the package field: the yellow of a missing cap is
          // carried by the sentence under the row alone.
          <div className={NUMBER_BOX}>
            <TextField
              type="number"
              inputMode="numeric"
              min={1}
              data-testid={PROTOCOL_TEST_IDS.ruleMaxTurns}
              label="Plafond de tours"
              hideLabel
              compact
              value={rules.maxTurns ?? ''}
              readOnly={readOnly}
              placeholder="…"
              onChange={(event) => onChangeRules(setMaxTurns(rules, event.currentTarget.value))}
            />
          </div>
        }
      />
      {rules.maxTurns === null ? (
        <p className="-mt-1 mb-1.5 inline-flex items-center gap-1.5 text-caption text-jaune-deep">
          <Icon name="warning" size={14} />
          sans plafond, l’enregistrement d’une version refusera cette étape
        </p>
      ) : null}
      <KeyValue
        label="réponses vides consécutives"
        sub="au-delà, le plafond est atteint"
        control={
          <div className={NUMBER_BOX}>
            <TextField
              type="number"
              inputMode="numeric"
              min={0}
              data-testid={PROTOCOL_TEST_IDS.ruleEmptyAnswers}
              label="Réponses vides consécutives"
              hideLabel
              compact
              value={rules.emptyAnswersAllowed}
              readOnly={readOnly}
              onChange={(event) => onChangeRules(setEmptyAnswers(rules, event.currentTarget.value))}
            />
          </div>
        }
      />
      <KeyValue
        label="nombre d’essais"
        sub="passages de l’étape en tout, 1 = pas de rejeu"
        control={
          <div className={NUMBER_BOX}>
            <TextField
              type="number"
              inputMode="numeric"
              min={1}
              data-testid={PROTOCOL_TEST_IDS.ruleAttempts}
              label="Nombre d’essais"
              hideLabel
              compact
              value={rules.attempts}
              readOnly={readOnly}
              onChange={(event) => onChangeRules(setAttempts(rules, event.currentTarget.value))}
            />
          </div>
        }
      />

      <p className="mt-3.5 mb-2 font-semibold text-gray-800 text-small">au-delà des essais</p>
      <div role="radiogroup" aria-label="Au-delà des essais" className="grid gap-2">
        <Tile
          testId={PROTOCOL_TEST_IDS.fallbackClose}
          title="fermer l’expérience"
          sub="aucune page suivante · défaut"
          checked={rules.fallback.kind === 'close-experience'}
          disabled={readOnly}
          onPick={() => onChangeRules(setFallback(rules, 'close-experience', []))}
        >
          {rules.fallback.kind === 'close-experience' ? (
            <div className="space-y-1">
              <TextField
                data-testid={PROTOCOL_TEST_IDS.fallbackText}
                label="Texte de clôture"
                hideLabel
                compact
                value={rules.fallback.text}
                readOnly={readOnly}
                maxLength={PROTOCOL_CLOSE_TEXT_MAX}
                onChange={(event) =>
                  onChangeRules(setFallbackText(rules, event.currentTarget.value))
                }
              />
              <div className="flex flex-wrap items-center justify-between gap-2 text-caption text-gray-600">
                <span>ce que la personne lit en dernier</span>
                {rules.fallback.text === PROTOCOL_DEFAULT_CLOSE_TEXT || readOnly ? (
                  <span>texte par défaut</span>
                ) : (
                  <button
                    type="button"
                    data-testid={PROTOCOL_TEST_IDS.fallbackTextDefault}
                    onClick={() =>
                      onChangeRules(setFallbackText(rules, PROTOCOL_DEFAULT_CLOSE_TEXT))
                    }
                    className="cursor-pointer text-bleu-nuit underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                  >
                    remettre le texte par défaut
                  </button>
                )}
              </div>
            </div>
          ) : null}
        </Tile>
        <Tile
          testId={PROTOCOL_TEST_IDS.fallbackGoTo}
          title="aller à une étape"
          sub={alone ? 'aucune autre étape' : 'choisir une étape, avant ou après'}
          checked={rules.fallback.kind === 'go-to-step'}
          disabled={readOnly || alone}
          onPick={() =>
            onChangeRules(
              setFallback(
                rules,
                'go-to-step',
                others.map((one) => one.id),
              ),
            )
          }
        >
          {rules.fallback.kind === 'go-to-step' ? (
            <div data-testid={PROTOCOL_TEST_IDS.fallbackTarget} className="max-w-full">
              <Select
                label="Étape de repli"
                hideLabel
                compact
                value={rules.fallback.stepId}
                disabled={readOnly}
                onValueChange={(stepId) => onChangeRules(setFallbackTarget(rules, stepId))}
                options={others.map((one) => ({
                  value: one.id,
                  label: stepTitle({ index: steps.indexOf(one), name: one.name }),
                }))}
              />
            </div>
          ) : null}
        </Tile>
      </div>

      <p
        data-testid={PROTOCOL_TEST_IDS.rulesRecap}
        className="mt-3.5 flex gap-2 rounded-md bg-gray-50 px-3 py-2.5 text-caption text-gray-700"
      >
        <span className="mt-0.5 inline-flex shrink-0 text-gray-500">
          <Icon name="info" size={14} />
        </span>
        {recap === null ? (
          <span className="text-gray-500">La phrase s’écrira quand le plafond sera posé.</span>
        ) : (
          <span>{recap}</span>
        )}
      </p>

      <p className="mt-2.5 text-caption text-gray-500">
        budget IA par étape : réglé dans{' '}
        <Link to="/admin/reglages" className="text-bleu-nuit underline underline-offset-2">
          Réglages
        </Link>
      </p>
    </div>
  );
}

/** One « clé : valeur » row of the panel (maquette `.kv`). */
function KeyValue({
  label,
  sub,
  control,
}: {
  label: string;
  sub: string;
  control: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-gray-100 border-t py-2 text-small first:border-t-0">
      <span className="min-w-0">
        {label}
        <span className="block text-caption text-gray-600">{sub}</span>
      </span>
      {control}
    </div>
  );
}

/**
 * One of the two repli tiles (maquette `.tile`).
 *
 * The `role="radio"` is on the HEADER of the tile and the extra control sits
 * beside it, never inside: a `<button>` may not contain a `<select>` or an
 * `<input>`, and a browser that flattens the nesting would leave the field
 * unclickable. The tile's border still moves as a whole, which is what the
 * artboard draws.
 */
function Tile({
  testId,
  title,
  sub,
  checked,
  disabled,
  onPick,
  children,
}: {
  testId: string;
  title: string;
  sub: string;
  checked: boolean;
  disabled: boolean;
  onPick: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className={`rounded-md border-2 p-3 ${checked ? 'border-bleu-nuit bg-bleu-nuit-tint' : 'border-gray-300 bg-white'}`}
    >
      <button
        type="button"
        // A `<button>` with `role="radio"` and not an `<input type="radio">`:
        // the tile carries a text field and a select beside its title, and a
        // `<label>` wrapping a real radio around those would forward a click on
        // the field to the radio. `aria-checked` says the same thing.
        // biome-ignore lint/a11y/useSemanticElements: see above
        role="radio"
        data-testid={testId}
        aria-checked={checked}
        disabled={disabled}
        onClick={onPick}
        className="flex w-full cursor-pointer items-start gap-2.5 text-left disabled:cursor-not-allowed"
      >
        <span
          aria-hidden="true"
          className={`mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border-2 bg-white ${checked ? 'border-bleu-nuit' : 'border-gray-400'}`}
        >
          {checked ? <span className="size-2 rounded-full bg-bleu-nuit" /> : null}
        </span>
        <span className="min-w-0">
          <span className={`block font-semibold text-small ${checked ? 'text-bleu-nuit' : ''}`}>
            {title}
          </span>
          <span className="block text-caption text-gray-600">{sub}</span>
        </span>
      </button>
      {children === null ? null : <div className="mt-2 pl-6.5">{children}</div>}
    </div>
  );
}
